import { useEffect, useMemo, useRef, useState } from "react";
import useAdminText from "../../../hooks/useAdminText";

const REPLACEABLE_ATTRIBUTES = ["placeholder", "title", "aria-label", "alt"] as const;
const SKIPPED_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA"]);

// Mapping from admin SPA pathname → screen slug(s) used in registry entries.
// Values may be a single slug string or an array of slugs (applied left-to-right, later wins).
// Paths are matched via longest-prefix rule in resolveCurrentScreen — see Change 1.
const ROUTE_TO_SCREEN: Record<string, string | string[]> = {
	"/": ["dashboard", "home"],
	"/booking": "bookings",
	"/user": "users",
	"/partner": "partners",
	"/partner-bulk-edit": "partners",
	"/station-requests": "station-requests",
	"/payment-channel-number": "payment-channels",
	"/content-manage": "content",
	"/text-editor": "text-registry",
	"/stations-text": "text-registry",
	"/homepage-text": "text-registry",
	"/admin-text": "text-registry",
	"/top-destination-manage": "top-destinations",
	"/format-email": "email-templates",
	"/debt-management": "debt",
	"/debt-detail": "debt",
	"/partner-debt-detail": "debt",
	"/debt-history": "debt",
	"/notifications": "notifications",
	"/country-rates": "country-rates",
	"/audit-logs": "audit",
	"/audit-timeline": "audit",
	"/station-portal-logs": "station-portal-logs",
	"/consent-logs": "consent-log",
	"/payment-cycle-settings": "payment-cycles",
	"/payment-surcharge-settings": "payment-surcharges",
	"/system-config": "system-config",
	"/service-interest-clicks": "service-interest",
	"/search-tracking": "search-tracking",
	"/support-categories": "support-config",
	"/404": "errors",
};

// Screen slugs that apply globally on every route (shared chrome: nav, sidebar, common dialogs).
const GLOBAL_SCREENS = new Set(["navigation", "layout", "controls", "components", "hooks", "config", "general"]);

// Custom event name dispatched after pushState / replaceState to detect SPA navigation.
const HISTORY_NAV_EVENT = "whalelo-admin-text-navigate";

// ---------------------------------------------------------------------------
// Preserved helpers — do not alter signatures or logic
// ---------------------------------------------------------------------------

function preserveWhitespace(source: string, replacement: string) {
	const match = source.match(/^(\s*)([\s\S]*?)(\s*)$/);
	return `${match?.[1] || ""}${replacement}${match?.[3] || ""}`;
}

function isSkippedElement(element: Element | null) {
	if (!element || !(element instanceof HTMLElement)) {
		return true;
	}

	return SKIPPED_TAGS.has(element.tagName) || element.isContentEditable;
}

// Change 3 — added optional outputValues param for cycle guard (see comment inside).
function resolveReplacement(
	value: string | null | undefined,
	replacements: Map<string, string>,
	outputValues?: Set<string>
): string | null {
	const originalValue = String(value || "");
	const normalizedValue = originalValue.trim();
	if (!normalizedValue) {
		return null;
	}

	// Cycle guard (Change 3): if this node's current text is itself an OUTPUT of some
	// replacement, skip it to prevent A→B→A runaway rAF cycles.
	if (outputValues && outputValues.has(normalizedValue)) {
		return null;
	}

	const replacement = replacements.get(normalizedValue);
	if (replacement === undefined || replacement === normalizedValue) {
		return null;
	}

	return preserveWhitespace(originalValue, replacement);
}

function applyElementAttributes(element: Element, replacements: Map<string, string>, outputValues?: Set<string>) {
	REPLACEABLE_ATTRIBUTES.forEach((attributeName) => {
		const currentValue = element.getAttribute(attributeName);
		const replacement = resolveReplacement(currentValue, replacements, outputValues);
		if (replacement !== null && replacement !== currentValue) {
			element.setAttribute(attributeName, replacement);
		}
	});

	if (
		element instanceof HTMLInputElement &&
		["button", "submit", "reset"].includes(String(element.type || "").toLowerCase())
	) {
		const replacement = resolveReplacement(element.value, replacements, outputValues);
		if (replacement !== null && replacement !== element.value) {
			element.value = replacement;
		}
	}
}

function applyNode(node: Node, replacements: Map<string, string>, outputValues?: Set<string>) {
	if (node.nodeType === Node.TEXT_NODE) {
		const parentElement = node.parentElement;
		if (isSkippedElement(parentElement)) {
			return;
		}

		const replacement = resolveReplacement(node.nodeValue, replacements, outputValues);
		if (replacement !== null && replacement !== node.nodeValue) {
			node.nodeValue = replacement;
		}
		return;
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return;
	}

	const element = node as Element;
	if (!isSkippedElement(element)) {
		applyElementAttributes(element, replacements, outputValues);
	}
}

function applyToTree(root: Node, replacements: Map<string, string>, outputValues?: Set<string>) {
	applyNode(root, replacements, outputValues);

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
	let currentNode = walker.nextNode();
	while (currentNode) {
		applyNode(currentNode, replacements, outputValues);
		currentNode = walker.nextNode();
	}
}

// ---------------------------------------------------------------------------
// Route + screen helpers (Change 1 + Change 2)
// ---------------------------------------------------------------------------

/**
 * Reads window.location.pathname and maps it to the list of registry screen slugs for that route.
 *
 * Change 1 — Longest-prefix matching:
 *   Finds the longest key K in ROUTE_TO_SCREEN such that pathname === K or
 *   pathname.startsWith(K + "/"). "/" only matches exactly (never as a prefix).
 *   Falls back to [firstSegment] for unknown routes, or ["general"] for root.
 *
 * Change 2a — Returns string[] (single-entry array for single-slug routes).
 *
 * SSR-safe: returns ["general"] when window is absent.
 */
function resolveCurrentScreen(): string[] {
	if (typeof window === "undefined") {
		return ["general"];
	}
	const pathname = window.location.pathname;
	// Strip trailing slash except on root "/".
	const normalized = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

	// Longest-prefix matching. Exact match wins immediately (break).
	// "/" is excluded from the prefix scan to prevent it matching every path.
	let bestKey: string | null = null;
	let bestLen = -1;
	for (const key of Object.keys(ROUTE_TO_SCREEN)) {
		if (key === normalized) {
			bestKey = key;
			break;
		}
		if (key !== "/" && normalized.startsWith(key + "/") && key.length > bestLen) {
			bestLen = key.length;
			bestKey = key;
		}
	}

	if (bestKey !== null) {
		const value = ROUTE_TO_SCREEN[bestKey];
		return Array.isArray(value) ? value : [value];
	}

	// Unknown route: derive slug from first path segment.
	const firstSegment = normalized.split("/").filter(Boolean)[0];
	return [firstSegment ? firstSegment.toLowerCase() : "general"];
}

/**
 * Change 2c — Builds the active replacement map for the given screen list.
 *
 * Priority (lowest → highest):
 *   baseMap (unique non-global) → globalMap (shared chrome) → screenIndex entries for each
 *   screen in order (duplicated, route-scoped; last screen in list wins on conflicts).
 */
function buildActiveReplacements(
	screens: string[],
	baseMap: Map<string, string>,
	globalMap: Map<string, string>,
	screenIndex: Map<string, Map<string, string>>
): Map<string, string> {
	const result = new Map<string, string>(baseMap);
	globalMap.forEach((value, key) => {
		result.set(key, value);
	});
	screens.forEach((screen) => {
		const screenMap = screenIndex.get(screen);
		if (screenMap) {
			screenMap.forEach((value, key) => {
				result.set(key, value);
			});
		}
	});
	return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminTextDomBridge() {
	const { registry } = useAdminText();

	// Change 2b — Build globalMap, baseMap, and screenIndex once per registry version.
	//
	// globalMap:   defaultText → currentText for GLOBAL_SCREENS entries (first-wins).
	// baseMap:     defaultText → currentText for non-global entries whose defaultText appears in
	//              exactly ONE non-global screen — unique across screens, applied on every route.
	// screenIndex: screenSlug → Map<defaultText, currentText> for non-global entries whose
	//              defaultText appears in ≥2 non-global screens — duplicated, route-scoped.
	const { screenIndex, globalMap, baseMap } = useMemo(() => {
		const nextGlobalMap = new Map<string, string>();
		// Per defaultText: Map<screen, firstCurrentText> accumulates first-wins per (defaultText, screen).
		const nonGlobalByText = new Map<string, Map<string, string>>();

		(registry?.items || []).forEach((entry) => {
			const defaultText = String(entry.defaultText || "").trim();
			const currentText = String(entry.currentText || "").trim();
			if (!defaultText || !currentText) {
				return;
			}
			const screen = String(entry.screen || "general").trim();

			if (GLOBAL_SCREENS.has(screen)) {
				if (!nextGlobalMap.has(defaultText)) {
					nextGlobalMap.set(defaultText, currentText);
				}
			} else {
				if (!nonGlobalByText.has(defaultText)) {
					nonGlobalByText.set(defaultText, new Map());
				}
				const byScreen = nonGlobalByText.get(defaultText)!;
				if (!byScreen.has(screen)) {
					byScreen.set(screen, currentText);
				}
			}
		});

		const nextBaseMap = new Map<string, string>();
		const nextScreenIndex = new Map<string, Map<string, string>>();

		nonGlobalByText.forEach((byScreen, defaultText) => {
			if (byScreen.size === 1) {
				// Unique — appears in exactly one non-global screen; safe to apply on any route.
				byScreen.forEach((currentText) => {
					nextBaseMap.set(defaultText, currentText);
				});
			} else {
				// Duplicated across multiple screens — route-scope each entry to avoid collisions.
				byScreen.forEach((currentText, screen) => {
					if (!nextScreenIndex.has(screen)) {
						nextScreenIndex.set(screen, new Map());
					}
					nextScreenIndex.get(screen)!.set(defaultText, currentText);
				});
			}
		});

		return { screenIndex: nextScreenIndex, globalMap: nextGlobalMap, baseMap: nextBaseMap };
	}, [registry]);

	// Change 2d — Current screen list; lazy-initialised from location at mount time.
	// resolveCurrentScreen is SSR-safe: returns ["general"] when window is absent.
	const [currentScreen, setCurrentScreen] = useState<string[]>(() =>
		typeof window === "undefined" ? ["general"] : resolveCurrentScreen()
	);

	// Active replacement map = baseMap → globalMap → current-screen duplicated entries.
	const activeReplacements = useMemo(
		() => buildActiveReplacements(currentScreen, baseMap, globalMap, screenIndex),
		[currentScreen, baseMap, globalMap, screenIndex]
	);

	// Mutable ref so the long-lived MutationObserver closure always reads the
	// latest map without needing to be re-created on every screen/registry change.
	const activeReplacementsRef = useRef<Map<string, string>>(activeReplacements);
	// Synchronous assignment during render — safe React pattern (no side-effects,
	// the ref is available to effects before they fire).
	activeReplacementsRef.current = activeReplacements;

	// Change 2d — Detect SPA navigation. The bridge is mounted outside the Router so we
	// cannot use react-router hooks. Patch history.pushState / replaceState to fire a
	// custom event; also listen for popstate (back/forward buttons).
	// Screen arrays compared by .join("|") to avoid unnecessary re-renders when unchanged.
	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const handleNavigate = () => {
			const next = resolveCurrentScreen();
			setCurrentScreen((prev) => (prev.join("|") === next.join("|") ? prev : next));
		};

		const origPushState = history.pushState;
		const origReplaceState = history.replaceState;

		history.pushState = (...args: Parameters<History["pushState"]>): void => {
			origPushState.apply(history, args);
			window.dispatchEvent(new Event(HISTORY_NAV_EVENT));
		};

		history.replaceState = (...args: Parameters<History["replaceState"]>): void => {
			origReplaceState.apply(history, args);
			window.dispatchEvent(new Event(HISTORY_NAV_EVENT));
		};

		window.addEventListener("popstate", handleNavigate);
		window.addEventListener(HISTORY_NAV_EVENT, handleNavigate);

		return () => {
			window.removeEventListener("popstate", handleNavigate);
			window.removeEventListener(HISTORY_NAV_EVENT, handleNavigate);
			history.pushState = origPushState;
			history.replaceState = origReplaceState;
		};
	}, []);

	// DOM bridge — observes document.body so MUI portals (Dialogs, Menus, Tooltips, etc.) are covered.
	// Change 3: outputValues Set built once per flush to prevent A→B→A replacement cycles.
	// Change 4: childList addedNodes → full subtree walk (applyToTree);
	//           attributes/characterData → single-node apply (applyNode).
	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const root = document.body;

		let frameId: number | null = null;
		// Change 4: two separate pending sets — subtree targets (childList) vs single-node targets.
		let pendingSubtreeTargets = new Set<Node>();
		let pendingNodeTargets = new Set<Node>();

		const flush = () => {
			frameId = null;
			const replacements = activeReplacementsRef.current;
			// Change 3: build outputValues once per flush (one Map values pass, not per node).
			const outputValues = new Set(replacements.values());

			// Change 4: swap both sets atomically so mutations during this flush accumulate
			// in fresh sets and are scheduled in a subsequent frame.
			const subtreeTargets = pendingSubtreeTargets;
			const nodeTargets = pendingNodeTargets;
			pendingSubtreeTargets = new Set();
			pendingNodeTargets = new Set();

			subtreeTargets.forEach((node) => {
				if (node === root || root.contains(node)) {
					applyToTree(node, replacements, outputValues);
				}
			});

			nodeTargets.forEach((node) => {
				if (node === root || root.contains(node)) {
					applyNode(node, replacements, outputValues);
				}
			});
		};

		const schedulePendingFlush = () => {
			if (frameId === null) {
				frameId = window.requestAnimationFrame(flush);
			}
		};

		// One synchronous full-tree pass on mount (before the observer starts).
		const initialOutputValues = new Set(activeReplacementsRef.current.values());
		applyToTree(root, activeReplacementsRef.current, initialOutputValues);

		const observer = new MutationObserver((records) => {
			for (const record of records) {
				if (record.type === "childList") {
					// Full subtree walk for newly added DOM subtrees.
					record.addedNodes.forEach((node) => pendingSubtreeTargets.add(node));
				} else {
					// attributes or characterData: single-node apply only (Change 4).
					pendingNodeTargets.add(record.target);
				}
			}
			if (pendingSubtreeTargets.size > 0 || pendingNodeTargets.size > 0) {
				schedulePendingFlush();
			}
		});

		observer.observe(root, {
			subtree: true,
			childList: true,
			characterData: true,
			attributes: true,
			attributeFilter: [...REPLACEABLE_ATTRIBUTES, "value"],
		});

		return () => {
			if (frameId !== null) {
				window.cancelAnimationFrame(frameId);
			}
			observer.disconnect();
		};
	}, []);

	// When active replacements change (screen navigation or registry update), reapply to the full
	// DOM so already-rendered nodes pick up the new values. Skips the very first render because
	// the DOM bridge effect above handles the initial full-tree pass.
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			return;
		}
		if (typeof window === "undefined") {
			return;
		}
		const replacements = activeReplacementsRef.current;
		const outputValues = new Set(replacements.values());
		applyToTree(document.body, replacements, outputValues);
	}, [activeReplacements]);

	return null;
}
